import { useState } from 'react';
import {
  Drawer,
  DrawerContent,
  DrawerHeader,
  DrawerTitle,
  DrawerTrigger,
} from '@monksflow/monks-ui';
import { SlidersHorizontalIcon } from 'lucide-react';
import { SearchInput } from '@/shared/ui/SearchInput';

import {
  buildInsightTypeOptions,
  buildOpportunityTypeOptions,
  chipClass,
} from './OpportunitiesRoomToolbar.constants';
import type { OpportunitiesRoomToolbarProps as Props } from './OpportunitiesRoomToolbar.types';
import { RadarlistToggle } from './RadarlistToggle';
import { displayMarketToken } from '@/entities/opportunity';
import { useTranslation } from 'react-i18next';

// ─── Filter group ─────────────────────────────────────────────────────────────

function FilterGroup<T extends string>({
  legend,
  options,
  value,
  onChange,
}: {
  legend: string;
  options: { value: T; label: string }[];
  value: T | null;
  onChange: (value: T | null) => void;
}) {
  const { t } = useTranslation('analysis');

  return (
    <div className="flex flex-col gap-2" role="group" aria-label={legend}>
      <span className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
        {legend}
      </span>
      <div className="flex flex-wrap gap-2">
        <button
          type="button"
          aria-pressed={value === null}
          onClick={() => onChange(null)}
          className={chipClass(value === null, true)}
        >
          {t('opportunitiesRoom.opportunitiesRoomToolbarMobile.all')}
        </button>
        {options.map((opt) => (
          <button
            key={opt.value}
            type="button"
            aria-pressed={value === opt.value}
            onClick={() => onChange(value === opt.value ? null : opt.value)}
            className={chipClass(value === opt.value, false)}
          >
            {opt.label}
          </button>
        ))}
      </div>
    </div>
  );
}

// ─── Component ────────────────────────────────────────────────────────────────

export function OpportunitiesRoomToolbarMobile({
  filters,
  opportunityType,
  radarlistOnly,
  marketOptions,
  audienceOptions,
  onSearch,
  onInsightTypeChange,
  onOpportunityTypeChange,
  onMarketChange,
  onAudienceChange,
  onRadarlistOnlyChange,
}: Props) {
  const { t } = useTranslation('analysis');

  const [drawerOpen, setDrawerOpen] = useState(false);

  const activeFilterCount = [
    filters.insightType,
    filters.market,
    filters.audience,
    opportunityType,
    radarlistOnly || null,
  ].filter(Boolean).length;

  return (
    <div className="flex justify-between items-center gap-3">
      <RadarlistToggle radarlistOnly={radarlistOnly} onChange={onRadarlistOnlyChange} />

      {/* Search + filter sheet trigger */}
      <div className="flex items-center gap-2">
        <SearchInput
          value={filters.search}
          onChange={onSearch}
          placeholder={t('opportunitiesRoom.opportunitiesRoomToolbarMobile.searchOpportunities')}
          aria-label={t(
            'opportunitiesRoom.opportunitiesRoomToolbarMobile.searchOpportunitiesLabel',
          )}
          size="sm"
          className="w-full"
        />

        <Drawer open={drawerOpen} onOpenChange={setDrawerOpen}>
          <DrawerTrigger asChild>
            <button
              type="button"
              aria-label={
                activeFilterCount > 0
                  ? t('opportunitiesRoom.opportunitiesRoomToolbarMobile.activeFilters', {
                      activeCount: activeFilterCount,
                    })
                  : t('opportunitiesRoom.opportunitiesRoomToolbarMobile.openFilters')
              }
              className="relative flex items-center justify-center size-9 rounded-lg border border-border bg-background shrink-0 hover:bg-accent transition-colors"
            >
              <SlidersHorizontalIcon className="size-4" />
              {activeFilterCount > 0 && (
                <span
                  aria-hidden="true"
                  className="absolute -top-1 -right-1 flex items-center justify-center size-4 rounded-full bg-foreground text-background text-[10px] font-medium"
                >
                  {activeFilterCount}
                </span>
              )}
            </button>
          </DrawerTrigger>
          <DrawerContent className="px-4 pb-10 max-h-[85dvh] overflow-y-auto">
            <DrawerHeader className="mb-5">
              <DrawerTitle>
                {t('opportunitiesRoom.opportunitiesRoomToolbarMobile.filters')}
              </DrawerTitle>
            </DrawerHeader>
            <div className="flex flex-col gap-6">
              <FilterGroup
                legend={t('opportunitiesRoom.opportunitiesRoomToolbarMobile.view')}
                options={[
                  {
                    value: 'radarlist',
                    label: t('opportunitiesRoom.opportunitiesRoomToolbarMobile.radarListOnly'),
                  },
                ]}
                value={radarlistOnly ? 'radarlist' : null}
                onChange={(v) => onRadarlistOnlyChange(v === 'radarlist')}
              />
              <FilterGroup
                legend={t('opportunitiesRoom.opportunitiesRoomToolbarMobile.insightType')}
                options={buildInsightTypeOptions()}
                value={filters.insightType}
                onChange={onInsightTypeChange}
              />
              <FilterGroup
                legend={t('opportunitiesRoom.opportunitiesRoomToolbarMobile.durationType')}
                options={buildOpportunityTypeOptions()}
                value={opportunityType}
                onChange={onOpportunityTypeChange}
              />
              {marketOptions.length > 0 && (
                <FilterGroup
                  legend={t('opportunitiesRoom.opportunitiesRoomToolbarMobile.market')}
                  options={marketOptions.map((m) => ({ value: m, label: displayMarketToken(m) }))}
                  value={filters.market}
                  onChange={onMarketChange}
                />
              )}
              {audienceOptions.length > 0 && (
                <FilterGroup
                  legend={t('opportunitiesRoom.opportunitiesRoomToolbarMobile.audience')}
                  options={audienceOptions.map((a) => ({ value: a, label: a }))}
                  value={filters.audience}
                  onChange={onAudienceChange}
                />
              )}
            </div>
          </DrawerContent>
        </Drawer>
      </div>
    </div>
  );
}
